import AddIcon from '@mui/icons-material/Add';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../auth/AuthContext';
import type { EntitySpec, RelationDef } from '../../models/Models';
import { api } from '../../services/Server';
import { formatApiError } from '../../utils/Error';
import ConfirmDialog from './ConfirmDialog';
import Editor from './Editor';
import { displayName, specForPath } from './helpers';
import { useRelationData } from './hooks';
import NdaBanner from './NdaBanner';
import ProfileTab from './Profile';
import type { AnyEntity, ConfirmState } from './types';

/**
 * Lazy-fetched full record viewer for a related item - the list endpoint
 * trims most columns for performance, so we issue a GET /{path}/{id} the
 * first time the row is shown and cache it via React Query.
 */
const RelatedItemView = ({
  listItem,
  targetSpec,
  targetPath,
  onEditGroup,
}: {
  listItem: AnyEntity;
  targetSpec: EntitySpec | undefined;
  targetPath: string;
  onEditGroup?: (group: string) => void;
}) => {
  const { data: full, isLoading } = useQuery({
    queryKey: ['get', targetPath, listItem.id],
    queryFn: () => api.get<AnyEntity>(targetPath, listItem.id),
    enabled: !!targetSpec,
  });
  const item = full ?? listItem;
  if (!targetSpec) {
    return (
      <Typography variant="body2" color="text.secondary">
        No spec available for this entity.
      </Typography>
    );
  }
  if (isLoading && !full) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  return <ProfileTab spec={targetSpec} item={item} onEdit={onEditGroup} />;
};

interface RelatedInlineProps {
  items: AnyEntity[];
  targetSpec: EntitySpec | undefined;
  targetPath: string;
  emptyLabel: string;
  onNew?: () => void;
  newLabel?: string;
  onEditGroup?: (item: AnyEntity, group: string) => void;
  onDelete?: (item: AnyEntity) => void;
  /** When true, render the peers as a flat chip list with no detail pane. */
  compact?: boolean;
  /** Controlled sub-tab selection (related item id within the tab). */
  subtab?: number | null;
  onSubtabChange?: (id: number | null) => void;
}

/**
 * Render the related rows in the tab. A single related item is shown
 * directly; multiple items render as a row of pill buttons (sub-tabs)
 * matching the legacy app's layout - clicking a pill swaps in that item's
 * Profile view below.
 */
const RelatedInline = ({
  items,
  targetSpec,
  targetPath,
  emptyLabel,
  onNew,
  newLabel = 'New',
  onEditGroup,
  onDelete,
  compact = false,
  subtab,
  onSubtabChange,
}: RelatedInlineProps) => {
  const [localSelectedId, setLocalSelectedId] = useState<number | null>(subtab ?? null);
  const selectedId = subtab ?? localSelectedId;
  const setSelectedId = (id: number | null) => {
    setLocalSelectedId(id);
    onSubtabChange?.(id);
  };

  // Reset selection only when the *set* of items actually changes - comparing
  // by id-signature avoids resetting whenever the parent re-renders and hands
  // us a freshly filtered array with the same contents.
  const idsKey = items.map((it) => it.id).join(',');
  useEffect(() => {
    const prev = selectedId;
    if (prev !== null && items.some((it) => it.id === prev)) return;
    const next = items[0]?.id ?? null;
    setSelectedId(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const selected = items.find((it) => it.id === selectedId) ?? items[0] ?? null;

  const toolbar = onNew && (
    <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onNew}>
      {newLabel}
    </Button>
  );

  if (compact) {
    return <CompactList items={items} targetSpec={targetSpec} onNew={onNew} newLabel={newLabel} onDelete={onDelete} emptyLabel={emptyLabel} />;
  }

  if (!items.length) {
    return (
      <Stack spacing={2}>
        {toolbar && <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>{toolbar}</Box>}

        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {emptyLabel}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflowX: 'auto',
            flex: 1,
            minWidth: 0,
            pb: 0.5,
            scrollbarWidth: 'thin',
          }}
        >
          {items.map((it) => {
            const active = it.id === selected!.id;
            return (
              <Chip
                key={it.id}
                label={displayName(it, targetSpec?.singular)}
                onClick={() => setSelectedId(it.id)}
                onDelete={onDelete ? () => onDelete(it) : undefined}
                clickable
                sx={{
                  flexShrink: 0,
                  fontWeight: 600,
                  bgcolor: active ? '#009DE0' : '#EDF1F7',
                  color: active ? '#fff' : '#002A5C',
                  border: active ? 'none' : '1px solid #DEE3EA',
                  '&:hover': {
                    bgcolor: active ? '#0087C3' : 'rgba(0,157,224,0.1)',
                  },
                  '& .MuiChip-deleteIcon': {
                    color: active ? 'rgba(255,255,255,0.7)' : '#8E99A8',
                    '&:hover': { color: active ? '#fff' : '#E30613' },
                  },
                }}
              />
            );
          })}
        </Box>
        {toolbar}
      </Box>
      <RelatedItemView
        key={selected!.id}
        listItem={selected!}
        targetSpec={targetSpec}
        targetPath={targetPath}
        onEditGroup={onEditGroup ? (group) => onEditGroup(selected!, group) : undefined}
      />
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// CompactList: simple chip list for managed M-N relations.
// ---------------------------------------------------------------------------

const CompactList = ({
  items,
  targetSpec,
  onNew,
  newLabel,
  onDelete,
  emptyLabel,
}: {
  items: AnyEntity[];
  targetSpec: EntitySpec | undefined;
  onNew?: () => void;
  newLabel: string;
  onDelete?: (item: AnyEntity) => void;
  emptyLabel: string;
}) => (
  <Stack spacing={1.5}>
    {onNew && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onNew}>
          {newLabel}
        </Button>
      </Box>
    )}
    {items.length === 0 ? (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        {emptyLabel}
      </Typography>
    ) : (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {items.map((it) => (
          <Chip
            key={it.id}
            label={displayName(it, targetSpec?.singular)}
            onDelete={onDelete ? () => onDelete(it) : undefined}
            sx={{
              fontWeight: 600,
              bgcolor: '#EDF1F7',
              color: '#002A5C',
              border: '1px solid #DEE3EA',
              '& .MuiChip-deleteIcon': {
                color: '#8E99A8',
                '&:hover': { color: '#E30613' },
              },
            }}
          />
        ))}
      </Box>
    )}
  </Stack>
);

const RelationTab = ({ rel, item, subtab, onSubtabChange }: { rel: RelationDef; item: AnyEntity; subtab?: number | null; onSubtabChange?: (id: number | null) => void }) => {
  const queryClient = useQueryClient();
  const { items, isLoading, targetPath, isJoin, isManaged, joinRows, otherOptions } =
    useRelationData(rel, item);
  const targetSpec = specForPath(targetPath);
  const [editing, setEditing] = useState<Partial<AnyEntity> | null>(null);
  const [editorGroup, setEditorGroup] = useState<string | undefined>(undefined);
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ConfirmState | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const { can } = useAuth();

  // Direct (1-N): we own the children - pre-fill the parent FK on create
  // and allow deleting the row outright.
  // Join (M-N, raw): expose the join table; chip-X removes the join row.
  // Managed (M-N, hidden): use a single PUT to replace the whole set.
  const isDirect = !isJoin && !isManaged && !!(rel.targetPath && rel.fkField);
  const managedPath = rel.managedPath?.(item.id);

  const invalidateAfterManagedSave = () => {
    queryClient.invalidateQueries({ queryKey: ['managed', managedPath] });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.remove(targetPath, id),
    onSuccess: (_v, id) => {
      toast.success(`${targetSpec?.singular ?? 'Item'} deleted`);
      queryClient.invalidateQueries({ queryKey: ['list', targetPath] });
      queryClient.removeQueries({ queryKey: ['get', targetPath, id] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const unlinkMutation = useMutation({
    mutationFn: (joinRowId: number) => api.remove(rel.joinPath!, joinRowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['list', rel.joinPath] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  // Managed: unlink one peer by re-PUTting the desired set without it.
  const unlinkManagedMutation = useMutation({
    mutationFn: (peerId: number) => {
      const next = items.filter((i) => i.id !== peerId).map((i) => i.id);
      return api.put<AnyEntity[], number[]>(managedPath!, next);
    },
    onSuccess: invalidateAfterManagedSave,
    onError: (e) => toast.error(formatApiError(e)),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const handleNew = isDirect && can('create')
    ? () => {
        setEditing({ [rel.fkField!]: item.id });
        setEditorOpen(true);
      }
    : (isJoin || isManaged) && can('update')
      ? () => setManageOpen(true)
      : undefined;

  const handleEditGroup = targetSpec && can('update')
    ? async (child: AnyEntity, group: string) => {
        try {
          const full = await queryClient.fetchQuery({
            queryKey: ['get', targetPath, child.id],
            queryFn: () => api.get<AnyEntity>(targetPath, child.id),
          });
          setEditing(full);
          setEditorGroup(group);
          setEditorOpen(true);
        } catch (e) {
          toast.error(formatApiError(e));
        }
      }
    : undefined;

  const handleDelete = isDirect && can('delete')
    ? (child: AnyEntity) => {
        const singular = targetSpec?.singular ?? 'item';
        const label = displayName(child, targetSpec?.singular);
        setConfirmDelete({
          title: `Delete ${singular}?`,
          message: `“${label}” will be permanently removed. This action cannot be undone.`,
          onConfirm: () => deleteMutation.mutate(child.id),
        });
      }
    : isJoin && can('update')
      ? (child: AnyEntity) => {
          const joinRow = joinRows.find((j) => j[rel.joinOtherField!] === child.id);
          if (joinRow) unlinkMutation.mutate(joinRow.id);
        }
      : isManaged && can('update')
        ? (child: AnyEntity) => unlinkManagedMutation.mutate(child.id)
        : undefined;

  return (
    <>
      {isDirect && rel.targetPath && rel.fkField && (
        <NdaBanner targetPath={rel.targetPath} fkField={rel.fkField} parentId={item.id} />
      )}
      <RelatedInline
        items={items}
        targetSpec={targetSpec}
        targetPath={targetPath}
        emptyLabel={`No ${rel.label.toLowerCase()} linked.`}
        onNew={handleNew}
        newLabel={isJoin || isManaged ? 'Manage' : 'New'}
        onEditGroup={handleEditGroup}
        onDelete={handleDelete}
        compact={isManaged}
        subtab={subtab}
        onSubtabChange={onSubtabChange}
      />
      {editorOpen && targetSpec && (
        <Editor
          spec={targetSpec}
          initial={editing}
          groupFilter={editorGroup}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['list', targetPath] });
            queryClient.invalidateQueries({ queryKey: ['get', targetPath] });
          }}
        />
      )}
      {manageOpen && isJoin && targetSpec && (
        <JoinManageDialog
          rel={rel}
          item={item}
          targetSpec={targetSpec}
          options={otherOptions}
          joinRows={joinRows}
          onClose={() => setManageOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['list', rel.joinPath] });
          }}
        />
      )}
      {manageOpen && isManaged && targetSpec && managedPath && (
        <ManagedDialog
          label={rel.label}
          targetSpec={targetSpec}
          options={otherOptions}
          current={items}
          managedPath={managedPath}
          onClose={() => setManageOpen(false)}
          onSaved={invalidateAfterManagedSave}
        />
      )}
      <ConfirmDialog state={confirmDelete} onClose={() => setConfirmDelete(null)} />
    </>
  );
};

// ---------------------------------------------------------------------------
// ManagedDialog: multi-select for relations exposed via a single PUT endpoint
// ---------------------------------------------------------------------------

const ManagedDialog = ({
  label,
  targetSpec,
  options,
  current,
  managedPath,
  onClose,
  onSaved,
}: {
  label: string;
  targetSpec: EntitySpec;
  options: AnyEntity[];
  current: AnyEntity[];
  managedPath: string;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [selected, setSelected] = useState<AnyEntity[]>(current);
  const [saving, setSaving] = useState(false);

  const sortedOptions = [...options].sort((a, b) =>
    displayName(a, targetSpec.singular).localeCompare(
      displayName(b, targetSpec.singular),
      undefined,
      { sensitivity: 'base' },
    ),
  );

  const save = async () => {
    setSaving(true);
    try {
      await api.put<AnyEntity[], number[]>(
        managedPath,
        selected.map((s) => s.id),
      );
      toast.success(`${label} updated`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage {label}</DialogTitle>
      <DialogContent dividers>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={sortedOptions}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionLabel={(o) => displayName(o, targetSpec.singular)}
          renderOption={(props, option, { selected: isSelected }) => (
            <li {...props} key={option.id}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={isSelected}
              />
              {displayName(option, targetSpec.singular)}
            </li>
          )}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getItemProps({ index });
              return (
                <Chip key={key} label={displayName(option, targetSpec.singular)} {...tagProps} />
              );
            })
          }
          renderInput={(params) => (
            <TextField {...params} label={targetSpec.label} placeholder="Add…" autoFocus />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="transparent-text-blue" disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// JoinManageDialog: multi-select editor for many-to-many relations
// ---------------------------------------------------------------------------

const JoinManageDialog = ({
  rel,
  item,
  targetSpec,
  options,
  joinRows,
  onClose,
  onSaved,
}: {
  rel: RelationDef;
  item: AnyEntity;
  targetSpec: EntitySpec;
  options: AnyEntity[];
  joinRows: AnyEntity[];
  onClose: () => void;
  onSaved: () => void;
}) => {
  // Map of currently linked peer-id → join-row-id, used when unlinking
  // (we need the join-row id to call DELETE /{joinPath}/{id}).
  const initialJoinByPeer = new Map<number, number>(
    joinRows.map((j) => [j[rel.joinOtherField!] as number, j.id]),
  );
  const [selected, setSelected] = useState<AnyEntity[]>(() =>
    options.filter((o) => initialJoinByPeer.has(o.id)),
  );
  const [saving, setSaving] = useState(false);

  // Keep selection in sync if the underlying data changes while the dialog
  // is open (e.g. another user added a link in the meantime).
  useEffect(() => {
    setSelected(options.filter((o) => initialJoinByPeer.has(o.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinRows.length, options.length]);

  const sortedOptions = [...options].sort((a, b) =>
    displayName(a, targetSpec.singular).localeCompare(
      displayName(b, targetSpec.singular),
      undefined,
      { sensitivity: 'base' },
    ),
  );

  const save = async () => {
    const selectedIds = new Set(selected.map((s) => s.id));
    const toAdd = [...selectedIds].filter((id) => !initialJoinByPeer.has(id));
    const toRemove = [...initialJoinByPeer.entries()]
      .filter(([peerId]) => !selectedIds.has(peerId))
      .map(([, joinId]) => joinId);

    if (toAdd.length === 0 && toRemove.length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await Promise.all([
        ...toAdd.map((peerId) =>
          api.create(rel.joinPath!, {
            [rel.joinSelfField!]: item.id,
            [rel.joinOtherField!]: peerId,
          }),
        ),
        ...toRemove.map((joinId) => api.remove(rel.joinPath!, joinId)),
      ]);
      toast.success(`${rel.label} updated`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage {rel.label}</DialogTitle>
      <DialogContent dividers>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={sortedOptions}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          getOptionLabel={(o) => displayName(o, targetSpec.singular)}
          renderOption={(props, option, { selected: isSelected }) => (
            <li {...props} key={option.id}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={isSelected}
              />
              {displayName(option, targetSpec.singular)}
            </li>
          )}
          renderValue={(value, getItemProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getItemProps({ index });
              return (
                <Chip
                  key={key}
                  label={displayName(option, targetSpec.singular)}
                  {...tagProps}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField {...params} label={targetSpec.label} placeholder="Add…" autoFocus />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="transparent-text-blue" disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RelationTab;
